import { activeCasesApi, newCasesApi } from '#entities';
import {
  advance,
  getCursor,
  REACT_APP_BE_API_URL,
  reportThrottled,
} from '#shared';

import {
  fetchWithAuthRecovery,
  isReportedAuthError,
  recoverFromAuthFailure,
} from '#shared/api';
import type { AuthTokenGetter } from '#shared/api';

import { applyEntityFrame } from './applyEntityFrame';
import { validateEntityFrame } from './serverMessage';

import type { AppDispatch } from '#shared';
import type { EntityFrame } from './serverMessage';

const REPORT_THROTTLE_MS = 60_000;
const CHANGES_PATH = 'api/cases/changes';

/**
 * Delta catch-up response. `entities`/`removed` are `unknown[]`, not `EntityFrame[]`/`string[]`
 * — the type guard only checks array-ness, so each item still needs per-item validation before use.
 */
interface CaseChangesDelta {
  resync?: false;
  entities: unknown[];
  removed: unknown[];
  cursor: string;
}

/** The server has no reliable delta for the given cursor — go fetch everything again. */
interface CaseChangesResync {
  resync: true;
  cursor: string;
}

type CaseChangesResponse = CaseChangesDelta | CaseChangesResync;

const isCaseChangesResponse = (
  value: unknown,
): value is CaseChangesResponse => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const p = value as Record<string, unknown>;
  if (typeof p.cursor !== 'string') {
    return false;
  }
  if (p.resync === true) {
    return true;
  }
  return Array.isArray(p.entities) && Array.isArray(p.removed);
};

/**
 * `GET api/cases/changes[?since=<cursor>]`; cursor omitted when still `''` (cold start).
 * No separator before the path — `REACT_APP_BE_API_URL` already carries a trailing slash (matches `fetchWithAuthBaseQuery`).
 */
const buildChangesUrl = (): string => {
  const base = `${REACT_APP_BE_API_URL ?? ''}${CHANGES_PATH}`;
  const cursor = getCursor('case');
  return cursor === '' ? base : `${base}?since=${encodeURIComponent(cursor)}`;
};

/**
 * Synthesizes the removal shape `applyEntityFrame` already drops from both caches (mirrors
 * a live `removed: true` frame), stamped with the batch's shared `cursor`.
 */
const buildRemovalFrame = (entityId: string, cursor: string): EntityFrame => ({
  kind: 'entity',
  entityType: 'case',
  entityId,
  list: null,
  entity: null,
  removed: true,
  ts: 0,
  cursor,
});

/** True if a settled resync refetch result represents a failure. */
const isFailedRefetch = (result: PromiseSettledResult<unknown>): boolean => {
  if (result.status === 'rejected') {
    return true;
  }
  const value = result.value as { error?: unknown; isError?: boolean } | null;
  return Boolean(value?.error) || value?.isError === true;
};

/**
 * `subscribe: false` + `forceRefetch` replaces the cache without leaking a lingering subscription.
 * `fetchWithAuthBaseQuery` swallows errors into a query-error cache entry rather than throwing,
 * so callers must inspect results (not try/catch) to know if both refetches succeeded.
 */
const resyncCaseSnapshots = async (dispatch: AppDispatch): Promise<boolean> => {
  const results = await Promise.allSettled([
    dispatch(
      activeCasesApi.endpoints.getActiveCases.initiate(undefined, {
        subscribe: false,
        forceRefetch: true,
      }),
    ),
    dispatch(
      newCasesApi.endpoints.getNewCases.initiate(undefined, {
        subscribe: false,
        forceRefetch: true,
      }),
    ),
  ]);
  return results.every(result => !isFailedRefetch(result));
};

/**
 * Reconnect entry point (from `socket.onopen`): fetches changes since the persisted cursor,
 * applies them, and advances the cursor. `resync: true` ⇒ server can't compute a delta — falls back to a full
 * re-fetch, advancing directly from its cursor since `seed()`'s "keep the older" logic would
 * wrongly preserve a stale one. Best-effort, never throws.
 */
export const runCaseCatchup = async (
  dispatch: AppDispatch,
  getToken: AuthTokenGetter,
): Promise<void> => {
  try {
    const token = await getToken();
    if (!token) {
      reportThrottled(
        'nop-events:catchup-no-token',
        'nop-events: catch-up skipped, no auth token',
        {
          throttleMs: REPORT_THROTTLE_MS,
          level: 'warning',
        },
      );
      return;
    }

    // Goes through the shared recovery so a mid-session expiry is refreshed and
    // retried in place — this raw-fetch path was invisible to the RTK Query base
    // query's recovery (TMDXNC-52).
    const res = await fetchWithAuthRecovery(buildChangesUrl(), token, {
      refreshToken: getToken,
    });
    if (!res.ok) {
      throw new Error(`nop-events: catch-up request failed (${res.status})`);
    }

    const payload: unknown = await res.json();
    if (!isCaseChangesResponse(payload)) {
      throw new Error('nop-events: catch-up response malformed');
    }

    if (payload.resync) {
      const resynced = await resyncCaseSnapshots(dispatch);
      if (!resynced) {
        // Both refetches failed — cache is still stale, so the cursor must NOT advance.
        // Leaving it put means the next reconnect retries instead of falsely believing it caught up.
        reportThrottled(
          'nop-events:resync-failed',
          'nop-events: case resync refetch failed',
          {
            throttleMs: REPORT_THROTTLE_MS,
            level: 'warning',
          },
        );
        return;
      }
      advance('case', payload.cursor);
      return;
    }

    // Validate every item like the live WS path — `applyEntityFrame` has no else-branch for
    // an unrecognized `list`, so an unvalidated item would silently misclassify as a new case.
    for (const item of payload.entities) {
      const frame = validateEntityFrame(item);
      if (!frame) {
        reportThrottled(
          'nop-events:bad-catchup-frame',
          'nop-events: dropped malformed catch-up entity frame',
          {
            throttleMs: REPORT_THROTTLE_MS,
            level: 'warning',
            extra: { item },
          },
        );
        continue;
      }
      applyEntityFrame(frame, dispatch);
    }
    for (const entityId of payload.removed) {
      if (typeof entityId !== 'string' || entityId.length === 0) {
        reportThrottled(
          'nop-events:bad-catchup-removed-id',
          'nop-events: dropped malformed catch-up removed id',
          {
            throttleMs: REPORT_THROTTLE_MS,
            level: 'warning',
            extra: { entityId },
          },
        );
        continue;
      }
      applyEntityFrame(buildRemovalFrame(entityId, payload.cursor), dispatch);
    }
    advance('case', payload.cursor);
  } catch (error) {
    if (isReportedAuthError(error)) {
      // An auth failure. It already emitted its own deduped event naming the
      // owner, so re-reporting it as a generic catch-up failure would only bury
      // the reason. Hand it to the shared guarded handlers instead of going quiet
      // (TMDXNC-52) — a session loss re-authenticates, a 403 routes to no-access.
      recoverFromAuthFailure(error);
      return;
    }
    reportThrottled(
      'nop-events:catchup-failed',
      'nop-events: catch-up failed',
      {
        throttleMs: REPORT_THROTTLE_MS,
        level: 'warning',
        extra: {
          error: error instanceof Error ? error.message : String(error),
        },
      },
    );
  }
};
